import { useMemo } from 'react';
import { CommentIcon, ContrastIcon, PersonOutlineIcon, ShieldIcon } from '@repo/atomic-components';
import { UserRoleType } from '@/api';
import { AccessControlAction, ADMIN_TOOLS_ALLOWED_USER_IDS } from '@/constants';
import { useAccessControl } from '@/hooks';
import { selectUserAboutData, useAppSelector } from '@/store';
import { ColourThemeControl } from './controls/ColourThemeControl';
import { FontControl, LineSpacingControl, TextSizeControl } from './controls/TextPreferenceControls';
import { SidebarModeControl } from './controls/SidebarModeControl';
import { EnterToSendControl } from './controls/EnterToSendControl';
import { AccountIdentityControl } from './controls/AccountIdentityControl';
import { OrganisationControl } from './controls/OrganisationControl';
import {
  AdminDashboardControl,
  BulkDeleteMattersControl,
  FeedbackExportsControl,
  UserManagementControl,
} from './controls/AdminControls';
import { BenchmarkingCloneControl, CAN_CLONE_CASES } from './controls/BenchmarkingCloneControl';
import type { SettingsGroup } from './types';

export const APPEARANCE_GROUP_ID = 'appearance';
export const ADMIN_GROUP_ID = 'admin';

/**
 * Everything the settings hub contains, in display order; the first group opens by default.
 * Settings the user can't access are filtered out here, so empty groups disappear and search skips them.
 */
export const useSettingsGroups = (): SettingsGroup[] => {
  const userAboutData = useAppSelector(selectUserAboutData);
  const canViewUserManagement = useAccessControl(AccessControlAction.VIEW_USER_MANAGEMENT_TAB);

  const isGlobalAdmin = userAboutData?.role === UserRoleType.GLOBAL_ADMIN;
  // Same visibility check the profile menu uses for its Admin row: global admins plus any
  // user explicitly allow-listed for the admin tools page.
  const canViewAdminTools =
    isGlobalAdmin || (!!userAboutData?.userId && ADMIN_TOOLS_ALLOWED_USER_IDS.includes(userAboutData.userId));

  return useMemo(() => {
    const accountSettings: SettingsGroup['settings'] = [
      {
        id: 'account-identity',
        title: 'Signed in as',
        keywords: ['profile', 'name', 'role', 'organisation', 'organization', 'who am i'],
        layout: 'block',
        Control: AccountIdentityControl,
      },
    ];

    if (isGlobalAdmin) {
      accountSettings.push({
        id: 'organisation',
        title: 'Organisation',
        description: 'Switch the organisation you are browsing. Fletch reloads after switching.',
        keywords: ['org', 'tenant', 'switch', 'company'],
        layout: 'block',
        Control: OrganisationControl,
      });
    }

    const groups: SettingsGroup[] = [
      {
        id: APPEARANCE_GROUP_ID,
        label: 'Appearance',
        description: 'How Fletch looks on this device.',
        Icon: ContrastIcon,
        settings: [
          {
            id: 'colour-theme',
            title: 'Colour theme',
            description: 'Bright, dark, or a softer off-white. Saved for this browser.',
            keywords: ['dark mode', 'light mode', 'night', 'contrast', 'colours', 'colors'],
            layout: 'block',
            Control: ColourThemeControl,
          },
          {
            id: 'text-size',
            title: 'Text size',
            description: 'Make text and icons across Fletch smaller or larger. Saved for this browser.',
            keywords: [
              'font size',
              'zoom',
              'bigger',
              'larger',
              'smaller',
              'accessibility',
              'low vision',
              'readability',
            ],
            layout: 'block',
            Control: TextSizeControl,
          },
          {
            id: 'font',
            title: 'Font',
            description:
              'Verdana has wider letters. OpenDyslexic is designed to make letters harder to mix up. Saved for this browser.',
            keywords: ['typeface', 'font family', 'dyslexia', 'dyslexic', 'accessibility', 'readability'],
            layout: 'block',
            Control: FontControl,
          },
          {
            id: 'line-spacing',
            title: 'Increased line spacing',
            description: 'Add more space between lines of text.',
            keywords: ['line height', 'leading', 'spacing', 'dyslexia', 'accessibility', 'readability'],
            Control: LineSpacingControl,
          },
          {
            id: 'sidebar-pinned',
            title: 'Keep the sidebar expanded',
            description: 'Pin the navigation sidebar open instead of expanding it when you hover over it.',
            keywords: ['navigation', 'pin', 'collapse', 'menu', 'rail'],
            Control: SidebarModeControl,
          },
        ],
      },
      {
        id: 'chat',
        label: 'Chat',
        description: 'How the chat composer behaves.',
        Icon: CommentIcon,
        settings: [
          {
            id: 'enter-to-send',
            title: 'Enter to send',
            description:
              'On: Enter sends, Shift + Enter or Ctrl/Cmd + Enter adds a new line. Off: Enter adds a new line, Ctrl/Cmd + Enter sends.',
            keywords: ['keyboard', 'shortcut', 'return', 'new line', 'newline', 'message', 'composer'],
            Control: EnterToSendControl,
          },
        ],
      },
      {
        id: 'account',
        label: 'Account',
        description: 'The account this browser is signed in as.',
        Icon: PersonOutlineIcon,
        settings: accountSettings,
      },
    ];

    const adminSettings: SettingsGroup['settings'] = [];

    if (canViewUserManagement) {
      adminSettings.push({
        id: 'user-management',
        title: 'Users',
        description: 'Add, edit and deactivate the people who can use Fletch.',
        keywords: ['user list', 'people', 'accounts', 'permissions', 'roles', 'deactivate'],
        layout: 'block',
        Control: UserManagementControl,
      });
    }

    if (canViewAdminTools) {
      adminSettings.push({
        id: 'admin-dashboard',
        title: 'Admin dashboard',
        description: 'Inspect the system prompt currently driving Fletch.',
        keywords: ['system prompt', 'tools', 'debug'],
        layout: 'block',
        Control: AdminDashboardControl,
      });
    }

    if (isGlobalAdmin) {
      adminSettings.push({
        id: 'bulk-delete-matters',
        title: 'Bulk delete matters',
        description: 'Permanently delete cases by pasting their case references or uploading them as a CSV.',
        keywords: ['delete', 'remove', 'cases', 'matters', 'case reference', 'csv', 'bulk'],
        layout: 'block',
        Control: BulkDeleteMattersControl,
      });
      adminSettings.push({
        id: 'feedback-exports',
        title: 'Feedback exports',
        description: 'Download AI response feedback, UX feedback and toolkit prompt requests as CSV.',
        keywords: ['csv', 'download', 'export', 'report', 'toolkit'],
        layout: 'block',
        Control: FeedbackExportsControl,
      });
      adminSettings.push({
        id: 'benchmarking-clone',
        title: CAN_CLONE_CASES ? 'Clone a chat or case' : 'Clone a chat',
        description: CAN_CLONE_CASES
          ? 'Copy a chat or case into the Benchmarks organisation, including their documents, to reproduce them there. To clone a case, you must be in the organisation it belongs to.'
          : 'Copy a chat, with its documents, into the Benchmarks organisation to reproduce it there.',
        keywords: [
          'benchmark',
          'copy',
          'duplicate',
          'reproduce',
          'chat',
          'org',
          ...(CAN_CLONE_CASES ? ['matter', 'case'] : []),
        ],
        layout: 'block',
        Control: BenchmarkingCloneControl,
      });
    }

    if (adminSettings.length > 0) {
      groups.push({
        id: ADMIN_GROUP_ID,
        label: 'Admin',
        description: 'Organisation-wide tools, available to administrators.',
        Icon: ShieldIcon,
        settings: adminSettings,
      });
    }

    return groups;
  }, [canViewAdminTools, canViewUserManagement, isGlobalAdmin]);
};
